import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdTagsOptions } from '@onefootprint/fetch-dashboard';
import { IcoDotsHorizontal16 } from '@onefootprint/icons';
import { Dropdown, IconButton, useShortcut } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WithEntityProps } from '@/entity/components/with-entity';
import usePermissions from '@/hooks/use-permissions';
import useSession from '@/hooks/use-session';
import { useOpenDatadog } from '../../hooks/use-open-datadog';
const EditTagsDialog = dynamic(() => import('../edit-tags-dialog'));
const EditVaultDrawer = dynamic(() => import('../edit-vault-dialog'));
const RunVerificationChecksDialog = dynamic(() => import('../run-verification-checks-dialog'));
const UploadDocDialog = dynamic(() => import('../upload-doc-dialog'));
const RequestMoreInfoDialog = dynamic(() => import('./components/request-more-info-dialog'));
const RunPlaybookDialog = dynamic(() => import('../run-playbook-dialog'));
const UpdateAuthDialog = dynamic(() => import('./components/update-auth-dialog'));
const DeactivateEntityDialog = dynamic(() => import('../deactivate-entity-dialog'));
const RedactDialog = dynamic(() => import('../redact-dialog'));
enum ActionDialog {
  Auth = 'auth',
  RequestMoreInfo = 'request-more-info',
  RunPlaybook = 'run-playbook',
  UploadDoc = 'upload-doc',
  EditTags = 'edit-tags',
  EditVault = 'edit-vault',
  RunVerificationChecks = 'run-verification-checks',
  Datadog = 'datadog',
  DeactivateEntity = 'deactivate-entity',
  Redact = 'redact',
  RedactDocumentImages = 'redact-document-images',
}
const SHORTCUTS: Omit<Record<ActionDialog, string[]>, ActionDialog.DeactivateEntity | ActionDialog.Redact | ActionDialog.RedactDocumentImages> = {
  [ActionDialog.Datadog]: ['Control', 'd'],
  [ActionDialog.EditTags]: ['Control', 'e'],
  [ActionDialog.EditVault]: ['Control', 'v'],
  [ActionDialog.UploadDoc]: ['Control', 'u'],
  [ActionDialog.RunVerificationChecks]: ['Control', 'j'],
  [ActionDialog.RequestMoreInfo]: ['Control', 'k'],
  [ActionDialog.RunPlaybook]: ['Control', 'r'],
  [ActionDialog.Auth]: ['Control', 'y']
};
const UserActions = t0 => {
  const $ = _c(142);
  const {
    entity
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "header.actions"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("user-details", t1);
  let t2;
  if ($[1] !== entity.id) {
    t2 = getEntitiesByFpIdTagsOptions({
      path: {
        fpId: entity.id
      }
    });
    $[1] = entity.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !!entity.id;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data: tags
  } = useQuery(t4);
  const [openDialog, setOpenDialog] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    data: t5
  } = useSession();
  const {
    user
  } = t5;
  const {
    hasPermission
  } = usePermissions();
  const {
    openDatadog,
    isEnabled: isOpenDatadogEnabled
  } = useOpenDatadog();
  let t6;
  if ($[6] !== entity.data) {
    t6 = entity.data.some(_temp);
    $[6] = entity.data;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const hasContactInfo = t6;
  const isRiskOrCxOpsFirmEmployee = !!user?.isFirmEmployee && !!(user?.isRiskOpsEmployee || user?.isCxOpsEmployee);
  let t7;
  if ($[8] !== entity.workflows || $[9] !== hasPermission) {
    t7 = hasPermission("manual_review") && entity.workflows.some(_temp2);
    $[8] = entity.workflows;
    $[9] = hasPermission;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const canUpdateAuth = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setOpenDialog(null);
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleCloseDialog = t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = dialog => () => {
      setDropdownOpen(false);
      setOpenDialog(dialog);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleDialogOpen = t9;
  let t10;
  let t11;
  if ($[13] !== hasPermission) {
    t10 = () => hasPermission("write_entities") && handleDialogOpen(ActionDialog.EditVault)();
    t11 = hasPermission("write_entities");
    $[13] = hasPermission;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  const t12 = !t11;
  let t13;
  if ($[16] !== t10 || $[17] !== t12) {
    t13 = {
      keys: SHORTCUTS[ActionDialog.EditVault],
      onShortcut: t10,
      disabled: t12
    };
    $[16] = t10;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  useShortcut(t13);
  let t14;
  let t15;
  if ($[19] !== hasPermission) {
    t14 = () => hasPermission("label_and_tag") && handleDialogOpen(ActionDialog.EditTags)();
    t15 = hasPermission("label_and_tag");
    $[19] = hasPermission;
    $[20] = t14;
    $[21] = t15;
  } else {
    t14 = $[20];
    t15 = $[21];
  }
  const t16 = !t15;
  let t17;
  if ($[22] !== t14 || $[23] !== t16) {
    t17 = {
      keys: SHORTCUTS[ActionDialog.EditTags],
      onShortcut: t14,
      disabled: t16
    };
    $[22] = t14;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  useShortcut(t17);
  let t18;
  let t19;
  if ($[25] !== hasPermission) {
    t18 = () => hasPermission("write_entities") && handleDialogOpen(ActionDialog.UploadDoc)();
    t19 = hasPermission("write_entities");
    $[25] = hasPermission;
    $[26] = t18;
    $[27] = t19;
  } else {
    t18 = $[26];
    t19 = $[27];
  }
  const t20 = !t19;
  let t21;
  if ($[28] !== t18 || $[29] !== t20) {
    t21 = {
      keys: SHORTCUTS[ActionDialog.UploadDoc],
      onShortcut: t18,
      disabled: t20
    };
    $[28] = t18;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  useShortcut(t21);
  let t22;
  let t23;
  if ($[31] !== hasPermission) {
    t22 = () => hasPermission("write_entities") && handleDialogOpen(ActionDialog.RunVerificationChecks)();
    t23 = hasPermission("write_entities");
    $[31] = hasPermission;
    $[32] = t22;
    $[33] = t23;
  } else {
    t22 = $[32];
    t23 = $[33];
  }
  const t24 = !t23;
  let t25;
  if ($[34] !== t22 || $[35] !== t24) {
    t25 = {
      keys: SHORTCUTS[ActionDialog.RunVerificationChecks],
      onShortcut: t22,
      disabled: t24
    };
    $[34] = t22;
    $[35] = t24;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  useShortcut(t25);
  let t26;
  let t27;
  if ($[37] !== hasPermission) {
    t26 = () => hasPermission("manual_review") && handleDialogOpen(ActionDialog.RequestMoreInfo)();
    t27 = hasPermission("manual_review");
    $[37] = hasPermission;
    $[38] = t26;
    $[39] = t27;
  } else {
    t26 = $[38];
    t27 = $[39];
  }
  const t28 = !t27;
  let t29;
  if ($[40] !== t26 || $[41] !== t28) {
    t29 = {
      keys: SHORTCUTS[ActionDialog.RequestMoreInfo],
      onShortcut: t26,
      disabled: t28
    };
    $[40] = t26;
    $[41] = t28;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  useShortcut(t29);
  let t30;
  let t31;
  if ($[43] !== hasPermission) {
    t30 = () => hasPermission("manual_review") && handleDialogOpen(ActionDialog.RunPlaybook)();
    t31 = hasPermission("manual_review");
    $[43] = hasPermission;
    $[44] = t30;
    $[45] = t31;
  } else {
    t30 = $[44];
    t31 = $[45];
  }
  const t32 = !t31;
  let t33;
  if ($[46] !== t30 || $[47] !== t32) {
    t33 = {
      keys: SHORTCUTS[ActionDialog.RunPlaybook],
      onShortcut: t30,
      disabled: t32
    };
    $[46] = t30;
    $[47] = t32;
    $[48] = t33;
  } else {
    t33 = $[48];
  }
  useShortcut(t33);
  let t34;
  if ($[49] !== openDatadog) {
    t34 = {
      keys: SHORTCUTS[ActionDialog.Datadog],
      onShortcut: openDatadog
    };
    $[49] = openDatadog;
    $[50] = t34;
  } else {
    t34 = $[50];
  }
  useShortcut(t34);
  let t35;
  if ($[51] !== canUpdateAuth) {
    t35 = () => canUpdateAuth && handleDialogOpen(ActionDialog.Auth)();
    $[51] = canUpdateAuth;
    $[52] = t35;
  } else {
    t35 = $[52];
  }
  const t36 = !canUpdateAuth;
  let t37;
  if ($[53] !== t35 || $[54] !== t36) {
    t37 = {
      keys: SHORTCUTS[ActionDialog.Auth],
      onShortcut: t35,
      disabled: t36
    };
    $[53] = t35;
    $[54] = t36;
    $[55] = t37;
  } else {
    t37 = $[55];
  }
  useShortcut(t37);
  let t38;
  if ($[56] !== t) {
    t38 = t(_temp3);
    $[56] = t;
    $[57] = t38;
  } else {
    t38 = $[57];
  }
  let t39;
  if ($[58] !== t38) {
    t39 = <Dropdown.Trigger asChild={true}><IconButton variant="outline" aria-label={t38} size="compact" icon={IcoDotsHorizontal16} /></Dropdown.Trigger>;
    $[58] = t38;
    $[59] = t39;
  } else {
    t39 = $[59];
  }
  let t40;
  if ($[60] !== t) {
    t40 = t(_temp4);
    $[60] = t;
    $[61] = t40;
  } else {
    t40 = $[61];
  }
  let t41;
  if ($[62] !== t40) {
    t41 = <Dropdown.GroupTitle>{t40}</Dropdown.GroupTitle>;
    $[62] = t40;
    $[63] = t41;
  } else {
    t41 = $[63];
  }
  let t42;
  if ($[64] !== hasPermission || $[65] !== t) {
    t42 = hasPermission("write_entities") && <Dropdown.Item shortcutHint={SHORTCUTS[ActionDialog.EditVault]} onSelect={handleDialogOpen(ActionDialog.EditVault)}>{t(_temp5)}</Dropdown.Item>;
    $[64] = hasPermission;
    $[65] = t;
    $[66] = t42;
  } else {
    t42 = $[66];
  }
  let t43;
  if ($[67] !== hasPermission || $[68] !== t || $[69] !== tags?.length) {
    t43 = hasPermission("label_and_tag") && <Dropdown.Item shortcutHint={SHORTCUTS[ActionDialog.EditTags]} onSelect={handleDialogOpen(ActionDialog.EditTags)}>{tags?.length ? t(_temp6) : t(_temp7)}</Dropdown.Item>;
    $[67] = hasPermission;
    $[68] = t;
    $[69] = tags?.length;
    $[70] = t43;
  } else {
    t43 = $[70];
  }
  let t44;
  if ($[71] !== hasPermission || $[72] !== t) {
    t44 = hasPermission("write_entities") && <Dropdown.Item shortcutHint={SHORTCUTS[ActionDialog.UploadDoc]} onSelect={handleDialogOpen(ActionDialog.UploadDoc)}>{t(_temp8)}</Dropdown.Item>;
    $[71] = hasPermission;
    $[72] = t;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  let t45;
  if ($[74] !== hasPermission || $[75] !== t) {
    t45 = hasPermission("write_entities") && <Dropdown.Item shortcutHint={SHORTCUTS[ActionDialog.RunVerificationChecks]} onSelect={handleDialogOpen(ActionDialog.RunVerificationChecks)}>{t(_temp9)}</Dropdown.Item>;
    $[74] = hasPermission;
    $[75] = t;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  let t46;
  if ($[77] !== t41 || $[78] !== t42 || $[79] !== t43 || $[80] !== t44 || $[81] !== t45) {
    t46 = <Dropdown.Group>{t41}{t42}{t43}{t44}{t45}</Dropdown.Group>;
    $[77] = t41;
    $[78] = t42;
    $[79] = t43;
    $[80] = t44;
    $[81] = t45;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  let t47;
  if ($[83] !== canUpdateAuth || $[84] !== hasContactInfo || $[85] !== hasPermission || $[86] !== t) {
    t47 = hasPermission("manual_review") && <><Dropdown.Divider /><Dropdown.Group><Dropdown.GroupTitle>{t(_temp10)}</Dropdown.GroupTitle>{hasContactInfo && <Dropdown.Item shortcutHint={SHORTCUTS[ActionDialog.RequestMoreInfo]} onSelect={handleDialogOpen(ActionDialog.RequestMoreInfo)}>{t(_temp11)}</Dropdown.Item>}<Dropdown.Item shortcutHint={SHORTCUTS[ActionDialog.RunPlaybook]} onSelect={handleDialogOpen(ActionDialog.RunPlaybook)}>{t(_temp12)}</Dropdown.Item>{canUpdateAuth && <Dropdown.Item shortcutHint={SHORTCUTS[ActionDialog.Auth]} onSelect={handleDialogOpen(ActionDialog.Auth)}>{t(_temp13)}</Dropdown.Item>}</Dropdown.Group></>;
    $[83] = canUpdateAuth;
    $[84] = hasContactInfo;
    $[85] = hasPermission;
    $[86] = t;
    $[87] = t47;
  } else {
    t47 = $[87];
  }
  let t48;
  if ($[88] !== isOpenDatadogEnabled || $[89] !== isRiskOrCxOpsFirmEmployee || $[90] !== openDatadog || $[91] !== t || $[92] !== user?.isFirmEmployee) {
    t48 = user?.isFirmEmployee && <><Dropdown.Divider /><Dropdown.Group><Dropdown.GroupTitle>{t(_temp14)}</Dropdown.GroupTitle>{isOpenDatadogEnabled && <Dropdown.Item shortcutHint={SHORTCUTS[ActionDialog.Datadog]} onSelect={openDatadog}>{t(_temp15)}</Dropdown.Item>}<Dropdown.Item onSelect={handleDialogOpen(ActionDialog.DeactivateEntity)}>{t(_temp16)}</Dropdown.Item>{isRiskOrCxOpsFirmEmployee && <><Dropdown.Item onSelect={handleDialogOpen(ActionDialog.Redact)}>{t(_temp17)}</Dropdown.Item><Dropdown.Item onSelect={handleDialogOpen(ActionDialog.RedactDocumentImages)}>{t(_temp18)}</Dropdown.Item></>}</Dropdown.Group></>;
    $[88] = isOpenDatadogEnabled;
    $[89] = isRiskOrCxOpsFirmEmployee;
    $[90] = openDatadog;
    $[91] = t;
    $[92] = user?.isFirmEmployee;
    $[93] = t48;
  } else {
    t48 = $[93];
  }
  let t49;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <Dropdown.Divider />;
    $[94] = t49;
  } else {
    t49 = $[94];
  }
  let t50;
  if ($[95] !== t46 || $[96] !== t47 || $[97] !== t48) {
    t50 = <Dropdown.Content align="end" sideOffset={8} className="min-w-[240px]">{t46}{t47}{t48}{t49}</Dropdown.Content>;
    $[95] = t46;
    $[96] = t47;
    $[97] = t48;
    $[98] = t50;
  } else {
    t50 = $[98];
  }
  let t51;
  if ($[99] !== dropdownOpen || $[100] !== t39 || $[101] !== t50) {
    t51 = <Dropdown.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>{t39}{t50}</Dropdown.Root>;
    $[99] = dropdownOpen;
    $[100] = t39;
    $[101] = t50;
    $[102] = t51;
  } else {
    t51 = $[102];
  }
  let t52;
  if ($[103] !== hasPermission || $[104] !== openDialog) {
    t52 = hasPermission("write_entities") && <EditVaultDrawer open={openDialog === ActionDialog.EditVault} onClose={handleCloseDialog} />;
    $[103] = hasPermission;
    $[104] = openDialog;
    $[105] = t52;
  } else {
    t52 = $[105];
  }
  let t53;
  if ($[106] !== hasPermission || $[107] !== openDialog) {
    t53 = hasPermission("manual_review") && <RequestMoreInfoDialog open={openDialog === ActionDialog.RequestMoreInfo} onClose={handleCloseDialog} />;
    $[106] = hasPermission;
    $[107] = openDialog;
    $[108] = t53;
  } else {
    t53 = $[108];
  }
  let t54;
  if ($[109] !== hasPermission || $[110] !== openDialog) {
    t54 = hasPermission("manual_review") && <RunPlaybookDialog entityKind="person" open={openDialog === ActionDialog.RunPlaybook} onClose={handleCloseDialog} />;
    $[109] = hasPermission;
    $[110] = openDialog;
    $[111] = t54;
  } else {
    t54 = $[111];
  }
  let t55;
  if ($[112] !== canUpdateAuth || $[113] !== openDialog) {
    t55 = canUpdateAuth && <UpdateAuthDialog open={openDialog === ActionDialog.Auth} onClose={handleCloseDialog} />;
    $[112] = canUpdateAuth;
    $[113] = openDialog;
    $[114] = t55;
  } else {
    t55 = $[114];
  }
  let t56;
  if ($[115] !== hasPermission || $[116] !== openDialog) {
    t56 = hasPermission("write_entities") && <UploadDocDialog open={openDialog === ActionDialog.UploadDoc} onClose={handleCloseDialog} />;
    $[115] = hasPermission;
    $[116] = openDialog;
    $[117] = t56;
  } else {
    t56 = $[117];
  }
  let t57;
  if ($[118] !== hasPermission || $[119] !== openDialog) {
    t57 = hasPermission("label_and_tag") && <EditTagsDialog open={openDialog === ActionDialog.EditTags} onClose={handleCloseDialog} />;
    $[118] = hasPermission;
    $[119] = openDialog;
    $[120] = t57;
  } else {
    t57 = $[120];
  }
  let t58;
  if ($[121] !== hasPermission || $[122] !== openDialog) {
    t58 = hasPermission("write_entities") && <RunVerificationChecksDialog open={openDialog === ActionDialog.RunVerificationChecks} onClose={handleCloseDialog} />;
    $[121] = hasPermission;
    $[122] = openDialog;
    $[123] = t58;
  } else {
    t58 = $[123];
  }
  const t59 = openDialog === ActionDialog.DeactivateEntity;
  let t60;
  if ($[124] !== t59) {
    t60 = <DeactivateEntityDialog open={t59} onClose={handleCloseDialog} />;
    $[124] = t59;
    $[125] = t60;
  } else {
    t60 = $[125];
  }
  let t61;
  if ($[126] !== isRiskOrCxOpsFirmEmployee || $[127] !== openDialog) {
    t61 = isRiskOrCxOpsFirmEmployee && <><RedactDialog kind="vault" open={openDialog === ActionDialog.Redact} onClose={handleCloseDialog} /><RedactDialog kind="documentImages" open={openDialog === ActionDialog.RedactDocumentImages} onClose={handleCloseDialog} /></>;
    $[126] = isRiskOrCxOpsFirmEmployee;
    $[127] = openDialog;
    $[128] = t61;
  } else {
    t61 = $[128];
  }
  let t62;
  if ($[129] !== t52 || $[130] !== t53 || $[131] !== t54 || $[132] !== t55 || $[133] !== t56 || $[134] !== t57 || $[135] !== t58 || $[136] !== t60 || $[137] !== t61) {
    t62 = <Suspense fallback={null}>{t52}{t53}{t54}{t55}{t56}{t57}{t58}{t60}{t61}</Suspense>;
    $[129] = t52;
    $[130] = t53;
    $[131] = t54;
    $[132] = t55;
    $[133] = t56;
    $[134] = t57;
    $[135] = t58;
    $[136] = t60;
    $[137] = t61;
    $[138] = t62;
  } else {
    t62 = $[138];
  }
  let t63;
  if ($[139] !== t51 || $[140] !== t62) {
    t63 = <>{t51}{t62}</>;
    $[139] = t51;
    $[140] = t62;
    $[141] = t63;
  } else {
    t63 = $[141];
  }
  return t63;
};
export default UserActions;
function _temp(d) {
  return ["id.phone_number", "id.email"].includes(d.identifier);
}
function _temp2(wf) {
  return wf.playbook?.requiresAuthentication;
}
function _temp3($) {
  return $.trigger;
}
function _temp4($_0) {
  return $_0.management.title;
}
function _temp5($_1) {
  return $_1.management.edit;
}
function _temp6($_2) {
  return $_2.management.editTags;
}
function _temp7($_3) {
  return $_3.management.addTags;
}
function _temp8($_4) {
  return $_4.management.uploadDocument;
}
function _temp9($_5) {
  return $_5.management.runVerificationChecks;
}
function _temp10($_6) {
  return $_6.requests.title;
}
function _temp11($_7) {
  return $_7.requests.requestMoreInfo;
}
function _temp12($_8) {
  return $_8.requests.runPlaybook;
}
function _temp13($_9) {
  return $_9.requests.allowUpdatingLoginMethods;
}
function _temp14($_10) {
  return $_10.internal.title;
}
function _temp15($_11) {
  return $_11.internal.datadog;
}
function _temp16($_12) {
  return $_12.internal.deactivateEntity;
}
function _temp17($_13) {
  return $_13.internal.redact;
}
function _temp18($_14) {
  return $_14.internal.redactDocumentImages;
}